/**
 *
 * Provides formatting controls for decklines including text formatting (bold, italic, underline),
 * borders, cell colors, alignment, and line thickness options.
 */

import './DecklineFormattingOptions.scss';

import { useDecklinesContext } from '@context/DecklinesContext';
import BorderInnerOutlined from '@mui/icons-material/BorderInnerOutlined';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatColorFill from '@mui/icons-material/FormatColorFill';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatItalic from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import LineStyleOutlined from '@mui/icons-material/LineStyleOutlined';
import TagIcon from '@mui/icons-material/Tag';
import { ToggleButtonGroup } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import { BorderWeight, CellFormat } from '@services/api/deckManagement/graphql/schema/API';
import { isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alignment,
  BorderType,
  CellColor,
  DecklineFormattingFields,
  DecklineFormattingOptionsProps,
  LineThickness,
  TextFormat,
} from 'types/DecklineFormatting.types';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { ContextMenuEnums } from 'types/Page.types';

import FormatMenu from './FormatMenu';
import { borderOptions, cellColorOptions, lineThicknessOptions, numberFormatOptions } from './Formatters';

/**
 * Interface for format configuration items
 * @see formatConfig
 */
interface FormatConfigItem {
  field?: keyof EnhancedDeckline;
  test?: (deckline: EnhancedDeckline) => boolean;
  value: string;
}

/**
 * Mapping of formatting configuration.
 * Include either `field` (an EnhancedDeckline prop) to do a simple null check, or include a `test` callback for more
 * advanced tests.
 *
 * When the test callback returns `true` or the `field` is NOT nil, we include the associated `value`
 * to the ToggleButtonGroup
 */
const formatConfig: FormatConfigItem[] = [
  {
    // Number Format
    test: (deckline) => !isNil(deckline.format_cell_default) && deckline.format_cell_default !== CellFormat.NONE,
    value: 'number-format-menu',
  },
  {
    field: DecklineFormattingFields.IS_FORMAT_BOLD,
    value: TextFormat.BOLD,
  },
  {
    field: DecklineFormattingFields.IS_FORMAT_ITALICS,
    value: TextFormat.ITALIC,
  },
  {
    field: DecklineFormattingFields.IS_FORMAT_UNDERLINE,
    value: TextFormat.UNDERLINE,
  },
  {
    field: DecklineFormattingFields.FORMAT_CELL_COLOR,
    value: 'cell-color-menu',
  },
  {
    // Indentation
    test: (deckline) => Boolean(deckline.kpi_name_indent_level && deckline.kpi_name_indent_level > 0),
    value: Alignment.INDENTATION,
  },
  {
    // Border Thickness/Weight
    test: (deckline) => deckline[DecklineFormattingFields.BORDER_WEIGHT_FLAG] !== BorderWeight.N,
    value: 'line-thickness-menu',
  },
  {
    // Border Enabled (Any)
    test: (deckline) =>
      Boolean(
        deckline.borderleftflag || deckline.borderbottomflag || deckline.bordertopflag || deckline.borderrightflag,
      ),
    value: 'border-format-menu',
  },
];

/**
 * DecklineFormattingOptions Component
 *
 * Provides formatting controls for decklines including text formatting (bold, italic, underline),
 * borders, cell colors, alignment, and line thickness options.
 *
 * @param onFormatChange - Callback function triggered when a formatting option is selected
 * @param selectedCell - Currently selected cell information containing row ID and field name
 */
const DecklineFormattingOptions = ({ onFormatChange, isEditingDisabled }: DecklineFormattingOptionsProps) => {
  const { updatePageDeckline, selectedDeckline } = useDecklinesContext();
  const [enabledFormats, setEnabledFormats] = useState<string[]>([]);

  /**
   * Synchronizes the selected Deckline's chosen formats, to be used for the ToggleButtonGroup value.
   * @see formatConfig
   */
  useEffect(() => {
    setEnabledFormats(() => {
      if (!selectedDeckline) {
        return [];
      }

      const formats: string[] = [];

      formatConfig.forEach((formatTestItem) => {
        if (formatTestItem.field) {
          const fieldValue = selectedDeckline[formatTestItem.field];
          if (fieldValue) {
            formats.push(formatTestItem.value);
          }
        } else if (formatTestItem.test && formatTestItem.test(selectedDeckline)) {
          formats.push(formatTestItem.value);
        }
      });

      return formats;
    });
  }, [selectedDeckline]);

  /**
   * Handles the click event for formatting options
   * @param formatType
   */
  const handleFormatClick = (
    formatType: Alignment | BorderType | CellColor | ContextMenuEnums | LineThickness | TextFormat,
  ) => {
    if (selectedDeckline) {
      const decklineId = selectedDeckline?.metadata?.internalId || selectedDeckline.sk!;

      if (selectedDeckline && decklineId) {
        switch (formatType) {
          case Alignment.INDENTATION:
            {
              const currentIndentLevel = selectedDeckline.kpi_name_indent_level ?? 0;
              const newIndentLevel = currentIndentLevel > 0 ? 0 : 1;
              updatePageDeckline(decklineId, 'kpi_name_indent_level', newIndentLevel);
              onFormatChange?.(formatType, newIndentLevel > 0);
            }
            break;
          case TextFormat.BOLD:
            updatePageDeckline(decklineId, DecklineFormattingFields.IS_FORMAT_BOLD, !selectedDeckline.is_format_bold);
            break;
          case TextFormat.ITALIC:
            updatePageDeckline(
              decklineId,
              DecklineFormattingFields.IS_FORMAT_ITALICS,
              !selectedDeckline.is_format_italics,
            );
            break;
          case TextFormat.UNDERLINE:
            updatePageDeckline(
              decklineId,
              DecklineFormattingFields.IS_FORMAT_UNDERLINE,
              !selectedDeckline.is_format_underline,
            );
            break;
          case BorderType.BOTTOM:
            updatePageDeckline(
              decklineId,
              DecklineFormattingFields.BORDER_BOTTOM_FLAG,
              !selectedDeckline.borderbottomflag,
            );
            break;
          case BorderType.TOP:
            updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_TOP_FLAG, !selectedDeckline.bordertopflag);
            break;
          case BorderType.LEFT:
            updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_LEFT_FLAG, !selectedDeckline.borderleftflag);
            break;
          case BorderType.RIGHT:
            updatePageDeckline(
              decklineId,
              DecklineFormattingFields.BORDER_RIGHT_FLAG,
              !selectedDeckline.borderrightflag,
            );
            break;
          case BorderType.ALL: {
            const allBordersActive =
              selectedDeckline.borderbottomflag &&
              selectedDeckline.bordertopflag &&
              selectedDeckline.borderleftflag &&
              selectedDeckline.borderrightflag;
            const newValue = !allBordersActive;
            updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_BOTTOM_FLAG, newValue);
            updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_TOP_FLAG, newValue);
            updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_LEFT_FLAG, newValue);
            updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_RIGHT_FLAG, newValue);
            // Toggling all borders off also clears the weight so no rogue weight lingers.
            if (!newValue) {
              updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_WEIGHT_FLAG, BorderWeight.N);
            }
            break;
          }
          case BorderType.NONE:
            updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_BOTTOM_FLAG, false);
            updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_TOP_FLAG, false);
            updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_LEFT_FLAG, false);
            updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_RIGHT_FLAG, false);
            updatePageDeckline(decklineId, DecklineFormattingFields.IS_FORMAT_BORDERLINE, false);
            // Clearing all borders must also reset the weight; a lingering weight
            // with no border is a "rogue" value the grid/export would otherwise honor.
            updatePageDeckline(decklineId, DecklineFormattingFields.BORDER_WEIGHT_FLAG, BorderWeight.N);
            break;
          case LineThickness.THIN:
            updatePageDeckline(
              decklineId,
              DecklineFormattingFields.BORDER_WEIGHT_FLAG,
              selectedDeckline.borderweightflag === BorderWeight.THIN ? BorderWeight.N : BorderWeight.THIN,
            );
            break;
          case LineThickness.THICK:
            updatePageDeckline(
              decklineId,
              DecklineFormattingFields.BORDER_WEIGHT_FLAG,
              selectedDeckline.borderweightflag === BorderWeight.THICK ? BorderWeight.N : BorderWeight.THICK,
            );
            break;
          case CellColor.WHITE:
          case CellColor.GREY:
            updatePageDeckline(decklineId, DecklineFormattingFields.FORMAT_CELL_COLOR, formatType.toString());
            break;
          default:
            if (onFormatChange) {
              onFormatChange(formatType);
            }
            break;
        }
      }
    }
  };

  /**
   * Handles the change event for number format options
   * @param newFormat
   */
  const handleChangeNumberFormat = useCallback(
    (newFormat: CellFormat | null) => {
      if (selectedDeckline) {
        const decklineId = selectedDeckline?.metadata?.internalId;
        updatePageDeckline(decklineId, DecklineFormattingFields.FORMAT_CELL_DEFAULT, newFormat);
      }
    },
    [selectedDeckline, updatePageDeckline],
  );

  /**
   * Memoized selected option from numberFormatOptions.
   */
  const selectedNumberFormat = useMemo(() => {
    if (selectedDeckline?.format_cell_default) {
      return numberFormatOptions.find((option) => option.value === selectedDeckline.format_cell_default);
    }
    return null;
  }, [selectedDeckline?.format_cell_default]);

  /**
   * Memoized tooltip for number format options.
   */
  const selectedNumberFormatTooltip = useMemo(() => {
    const selectedVal = selectedNumberFormat?.label ? ` (${selectedNumberFormat?.label})` : '';
    return `Cell Format${selectedVal}`;
  }, [selectedNumberFormat]);

  /**
   * The border options to show as selected in the border menu.
   *
   * Borders are four independent flags, so this is a set rather than one value — the menu previously
   * received nothing and so never highlighted anything. ALL and NONE are derived rather than stored:
   * they mirror what `handleFormatClick` does when either is picked, so the menu reflects the state
   * those entries would produce.
   */
  const selectedBorderTypes = useMemo(() => {
    if (!selectedDeckline) return [];

    const sides: [BorderType, boolean][] = [
      [BorderType.BOTTOM, Boolean(selectedDeckline.borderbottomflag)],
      [BorderType.TOP, Boolean(selectedDeckline.bordertopflag)],
      [BorderType.LEFT, Boolean(selectedDeckline.borderleftflag)],
      [BorderType.RIGHT, Boolean(selectedDeckline.borderrightflag)],
    ];

    const enabled = sides.filter(([, isOn]) => isOn).map(([side]) => side);
    if (enabled.length === sides.length) return [...enabled, BorderType.ALL];
    if (enabled.length === 0) return [BorderType.NONE];
    return enabled;
  }, [selectedDeckline]);

  /**
   * The line-thickness option to show as selected, from the deckline's border weight.
   *
   * `BorderWeight.N` highlights nothing, because it is the "no weight" sentinel everywhere else:
   * toggling a weight off writes it, clearing all borders writes it, and `useDecklineGridStyling`
   * pushes neither `thin-line` nor `thick-line` for it — so the grid shows no explicit weight
   * either. The enum carries no schema description, so this is from usage rather than a spec.
   */
  const selectedLineThickness = useMemo(() => {
    switch (selectedDeckline?.borderweightflag) {
      case BorderWeight.THIN:
        return LineThickness.THIN;
      case BorderWeight.THICK:
        return LineThickness.THICK;
      default:
        return undefined;
    }
  }, [selectedDeckline?.borderweightflag]);

  /**
   * Memoized selected cell color
   */
  const selectedCellColor = useMemo(() => {
    if (selectedDeckline?.format_cell_color) {
      return cellColorOptions.find((option) => option.value === selectedDeckline.format_cell_color);
    }
    return null;
  }, [selectedDeckline?.format_cell_color]);

  /**
   * Memoized tooltip for selected cell color
   */
  const selectedCellColorTooltip = useMemo(() => {
    const selectedVal = selectedCellColor?.label ? ` (${selectedCellColor?.label})` : '';
    return `Cell BG Color${selectedVal}`;
  }, [selectedCellColor]);

  return (
    <Box className='deckline-formatting-container'>
      <ToggleButtonGroup
        aria-label='text formatting'
        size='small'
        className='formatting-button-group'
        value={enabledFormats}
        disabled={isEditingDisabled}
      >
        <FormatMenu
          buttonValue='number-format-menu'
          buttonAriaLabel='number-format-menu'
          buttonIcon={selectedNumberFormat?.icon || <TagIcon />}
          options={numberFormatOptions}
          onFormatSelect={handleChangeNumberFormat}
          selectedValue={(() => {
            return selectedDeckline ? selectedDeckline.format_cell_default : undefined;
          })()}
          tooltip={selectedNumberFormatTooltip}
          isEditingDisabled={isEditingDisabled}
        />
        <Tooltip title='Indentation'>
          <Box component='span' className='format-button-wrapper'>
            <ToggleButton
              value={Alignment.INDENTATION}
              aria-label={Alignment.INDENTATION}
              onClick={() => handleFormatClick(Alignment.INDENTATION)}
              disabled={isEditingDisabled}
            >
              <FormatIndentIncreaseIcon />
            </ToggleButton>
          </Box>
        </Tooltip>
        <Divider orientation='vertical' variant='middle' flexItem className='format-vertical-divider' />
        <Tooltip title='Bold'>
          <Box component='span' className='format-button-wrapper'>
            <ToggleButton
              value={TextFormat.BOLD}
              aria-label={TextFormat.BOLD}
              onClick={() => handleFormatClick(TextFormat.BOLD)}
              disabled={isEditingDisabled}
            >
              <FormatBoldIcon />
            </ToggleButton>
          </Box>
        </Tooltip>
        <Tooltip title='Italicize'>
          <Box component='span' className='format-button-wrapper'>
            <ToggleButton
              value={TextFormat.ITALIC}
              aria-label={TextFormat.ITALIC}
              onClick={() => handleFormatClick(TextFormat.ITALIC)}
              disabled={isEditingDisabled}
            >
              <FormatItalic />
            </ToggleButton>
          </Box>
        </Tooltip>
        <Tooltip title='Underline'>
          <Box component='span' className='format-button-wrapper'>
            <ToggleButton
              value={TextFormat.UNDERLINE}
              aria-label={TextFormat.UNDERLINE}
              onClick={() => handleFormatClick(TextFormat.UNDERLINE)}
              disabled={isEditingDisabled}
            >
              <FormatUnderlinedIcon />
            </ToggleButton>
          </Box>
        </Tooltip>
        <FormatMenu
          buttonValue='cell-color-menu'
          buttonAriaLabel='cell-color-menu'
          buttonIcon={selectedCellColor?.icon || <FormatColorFill />}
          options={cellColorOptions}
          onFormatSelect={handleFormatClick}
          selectedValue={selectedCellColor?.value}
          tooltip={selectedCellColorTooltip}
          isEditingDisabled={isEditingDisabled}
        />
        <Divider orientation='vertical' variant='middle' flexItem className='format-vertical-divider' />
        {/* Borders Formatting */}
        <FormatMenu
          buttonValue='border-format-menu'
          buttonAriaLabel='border-format-menu'
          buttonIcon={<BorderInnerOutlined />}
          options={borderOptions}
          onFormatSelect={handleFormatClick}
          selectedValues={selectedBorderTypes}
          tooltip='Border Options'
          isEditingDisabled={isEditingDisabled}
        />
        <FormatMenu
          buttonValue='line-thickness-menu'
          buttonAriaLabel='line-thickness-menu'
          buttonIcon={<LineStyleOutlined />}
          options={lineThicknessOptions}
          onFormatSelect={handleFormatClick}
          selectedValue={selectedLineThickness}
          tooltip='Line Thickness'
          isEditingDisabled={isEditingDisabled}
        />
      </ToggleButtonGroup>
    </Box>
  );
};

export default DecklineFormattingOptions;
